import type {
  BlockMarkRendererMap,
  BlockStylesRendererMap,
  BlockTypeRendererMap,
  ListLevelRenderMap,
  ListStyleRendererMap,
  PortableTextSimpleQueryResult,
} from '@/components/portable-text/portable-text-simple-types';
import { buttonVariants } from '@/components/ui/button';
import { CustomLink } from '@veranda/shared/components/custom-link';
import { CustomTable } from '@veranda/shared/components/custom-table';
import { cleanPortableTextMarks } from '@veranda/shared/components/portable-text';
import { cn } from '@veranda/shared/lib';
import {
  type PortableTextBlock,
  PortableText as PortableTextComponent,
} from 'next-sanity';

const block = (className?: string): BlockStylesRendererMap => {
  return {
    h2: ({ children }) => {
      return (
        <h2 className={cn('font-inherit text-inherit', className)}>
          {children}
        </h2>
      );
    },
    h3: ({ children }) => {
      return (
        <h3 className={cn('font-inherit text-inherit', className)}>
          {children}
        </h3>
      );
    },
    sm: ({ children }) => {
      return (
        <p
          data-size="sm"
          className={cn('font-inherit text-inherit', className)}
        >
          {children}
        </p>
      );
    },
    normal: ({ children }) => {
      if (
        !children ||
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        !(children as any).some((c: any) => c.props?.text || c.length > 0)
      )
        return null; // Don't render empty p tags

      return (
        <p className={cn('font-inherit text-inherit', className)}>{children}</p>
      );
    },
  };
};

const marks = ({
  className,
}: { className?: string }): BlockMarkRendererMap<
  'linkWithoutText' | 'strong' | 'em' | 'underline'
> => {
  return {
    linkWithoutText: ({ children, value }) => {
      return (
        <CustomLink
          href={value?.slug ?? '#'}
          target={value?.openInNewTab ? '_blank' : undefined}
          className={cn('underline', className)}
        >
          {children}
        </CustomLink>
      );
    },
    strong: ({ children }) => {
      return (
        <strong className={cn('font-inherit text-inherit', className)}>
          {children}
        </strong>
      );
    },
    em: ({ children }) => {
      return (
        <em className={cn('font-inherit text-inherit', className)}>
          {children}
        </em>
      );
    },
    underline: ({ children }) => {
      return <span className={cn('underline', className)}>{children}</span>;
    },
  };
};

const list = (className?: string): ListStyleRendererMap => {
  return {
    bullet: ({ children }) => (
      <ul
        className={cn(
          'list-inside list-disc font-inherit text-inherit',
          className
        )}
      >
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol
        className={cn(
          'list-inside list-decimal font-inherit text-inherit',
          className
        )}
      >
        {children}
      </ol>
    ),
  };
};

const listItemStyle = 'font-inherit text-inherit';
const subLevelListItemStyle = 'first:mt-[0.35em]';

const listItem = (className?: string): ListLevelRenderMap => {
  return {
    bullet: ({ children, value: { level } }) => {
      return (
        <li
          className={cn(
            listItemStyle,
            level && level > 1 && subLevelListItemStyle,
            className
          )}
        >
          {children}
        </li>
      );
    },
    number: ({ children, value: { level } }) => {
      return (
        <li
          className={cn(
            listItemStyle,
            level && level > 1 && subLevelListItemStyle,
            className
          )}
        >
          {children}
        </li>
      );
    },
  };
};

const types = (className?: string): BlockTypeRendererMap => {
  const components: BlockTypeRendererMap = {
    linkButton: ({ value }) => {
      if (!value) return null;

      const alignmentClass = {
        left: 'flex justify-start',
        center: 'flex justify-center',
        right: 'flex justify-end',
      }[value?.alignment || 'left'];

      return (
        <div
          className={cn(className, alignmentClass, 'w-full overflow-hidden')}
        >
          <CustomLink
            href={value?.link?.slug ?? '#'}
            target={value?.link?.openInNewTab ? '_blank' : undefined}
            className={cn(
              buttonVariants({ variant: value?.buttonVariant }),
              'inline-block max-w-full overflow-hidden text-ellipsis',
              className
            )}
            id="portable-text-button"
          >
            <span className="block truncate">{value?.link?.text}</span>
          </CustomLink>
        </div>
      );
    },
    table: ({ value }) => {
      if (!value) return null;

      return (
        <CustomTable
          data={value}
          style={{ color: 'var(--color-brand-burgundy)' }}
        />
      );
    },
  };

  return components;
};

const components = ({ className }: { className?: string }) => {
  return {
    marks: marks({ className }),
    block: block(className),
    list: list(className),
    listItem: listItem(className),
    types: types(className),
  };
};

/**
 * Used for blog posts
 */
export const PortableTextSimple = (props: {
  content: NonNullable<PortableTextSimpleQueryResult>['content'] | null;
  className?: string;
}) => {
  const { content, className } = props;

  if (!content) return null;

  const cleanedContent = cleanPortableTextMarks(content as PortableTextBlock[]);

  return (
    <PortableTextComponent
      components={components({ className })}
      value={cleanedContent as PortableTextBlock[]}
    />
  );
};
